import { Tooltip } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { EllipsisContainer } from 'src/components/EllipsisContainer/EllipsisContainer';
import { EntityStatusInfo } from 'src/components/EntityStatusInfo/EntityStatusInfo';
import {
    MainContainer,
    MainEntityListItemText,
    MasterDetailsAsideItem,
    SecondaryContainer,
    SecondaryEntityListItemText,
} from 'src/components/MasterDetailsPage/MasterDetailsAsideItem';
import { EntityLinks } from 'src/core/router/EntityLinks';
import { LegalEntity, LoanDtoBrief, Person } from 'src/generated-api-client';
import { AgentLoanStatusInfoMap } from 'src/pages/AgentLoansPage/AgentLoanStatusInfoMap';
import { SettingsStore } from 'src/stores/SettingsStore/SettingsStore';
import { FullNameHelper } from 'src/utils/FullNameHelper';
import { NumberHelper } from 'src/utils/NumberHelper';

type AgentLoanListItemProps = { item: LoanDtoBrief };

export const AgentLoanListItem = observer(
    ({ item }: AgentLoanListItemProps) => {
        const { t, i18n } = useTranslation();
        const currencyMnemonic = SettingsStore.currencyMnemonic as string;

        const fullName = FullNameHelper(
            item?.loanApplication?.borrower as LegalEntity | Person,
        );

        return (
            <MasterDetailsAsideItem
                to={EntityLinks.agentLoans.itemDetails(item.id)}
            >
                <MainContainer>
                    <Tooltip title={fullName}>
                        <EllipsisContainer>
                            <MainEntityListItemText
                                className="strong-text"
                                data-testid="loan-number"
                            >{`${item?.loanApplication?.number} `}</MainEntityListItemText>
                            <MainEntityListItemText data-testid="borrower-name">
                                {fullName}
                            </MainEntityListItemText>
                        </EllipsisContainer>
                    </Tooltip>
                    <EntityStatusInfo
                        statusInfoMap={AgentLoanStatusInfoMap}
                        status={item.status}
                        entityName="loan"
                    />
                </MainContainer>
                <SecondaryContainer>
                    <SecondaryEntityListItemText>
                        <span
                            className="strong-text"
                            data-testid="amountApproved"
                        >
                            {NumberHelper.currencyFormatter(
                                item?.loanApplication?.amountApproved || 0,
                                { currencyMnemonic, locale: i18n.language },
                            )}
                        </span>
                    </SecondaryEntityListItemText>
                    <SecondaryEntityListItemText data-testid="term">
                        {item?.term}{' '}
                        {t(
                            `loan.period.type.${item?.loanApplication?.termPeriodType}`,
                        )}
                    </SecondaryEntityListItemText>
                    <SecondaryEntityListItemText data-testid="code">
                        {item?.loanProduct?.code}
                    </SecondaryEntityListItemText>
                </SecondaryContainer>
            </MasterDetailsAsideItem>
        );
    },
);
